import InputTextArea from "../ui/InputTextArea.js";
import LoadingMediaArea from "../ui/LoadingMediaArea.js";
import AlertArea from "../ui/AlertArea.js";
import BottomArea from "../ui/BottomArea.js";

import Grid from "./Grid.js";
import Voronoi from "./Voronoi.js";
import Transform from "./Transform.js";
import Geonodes from "./Geonodes.js";
import MapGis from "./MapGis.js";
import {DIRECTION_NORTH_WEST, DIRECTION_SOUTH_WEST, DIRECTION_SOUTH_EAST, DIRECTION_NORTH_EAST} from "./Axis.js";

import Error from "../utils/Error.js";
import global from "../global.js";

const log = global.log.getLogger("f_mtac.scene.map");
log.setLevel(log.levels.WARN);

let MapImageNode = function (view) {
    var image = new Image();
    var brightness = 1; // cache
    var image_width, image_height; // cache
    var image_rotation = 0.0; // degrees, cache

    log.debug("MapImageNode", {view});

    image.id = "background_image";
    image.style.position = "absolute";

    // Create an image_container element that handles the transformation translate and scale
    // The image element handles the rotation
    // They must be separated otherwise there are problem with the focal point during zoom

    this.image_container = document.createElement("div");
    view.container.appendChild(this.image_container);
    this.image_container.append(image);

    this.set_media = ({id, src, width, height}) => {
        log.debug("set_media", {id, src, width, height});
        image.dataset.media_id = id;
        image.src = src;
        image_width = width;
        image.width = image_width;
        image_height = height;
        image.height = image_height;
        this.rotation = 0.0;
    };

    this.unset_media = () => {
        log.debug("unset_media");
        delete image.dataset.media_id;
        image.removeAttribute("src");
        image.removeAttribute("width");
        image_width = undefined;
        image.removeAttribute("height");
        image_height = undefined;
        this.brightness = 1;
    };

    Object.defineProperties(this, {
        rotation: {
            set: (value) => {
                image_rotation = value;
                //let {x:xx, y:yy} = global.view.get_centering_parameters({width:image_width, height:image_height});
                // apply rotation from the center of the image
                if (image_rotation != 0.0) {
                    image.style.transformOrigin = `${image_width / 2.0}px ${image_height / 2.0}px`;
                    image.style.transform = `rotate(${image_rotation}deg)`;
                } else {
                    image.style.transformOrigin = "";
                    image.style.transform = "";
                }
            },
            get: () => image_rotation
        },
        width: {
            set: () => {},
            get: () => image_width
        },
        height: {
            set: () => {},
            get: () => image_height
        },
        media_id: {
            set: () => {},
            get: () => image.dataset.media_id
        },
        brightness: {
            set: (new_brightness) => {
                brightness = new_brightness;
                image.style.filter = `brightness(${brightness})`;
            },
            get: () => brightness
        },
        visibility: {
            set: (value) => {
                image.style.visibility = value;
            }
        }
    });

    this.transform = new Transform({x: 0, y: 0, k: 1});
    this.transform.on_change(({x, y, k}) => {
        this.image_container.style.transform = `translate(${x}px, ${y}px) scale(${k})`;
    });

    this.image_container.style.transformOrigin = `-${view.padding.left}px -${view.padding.top}px`;
    this.image_container.style.zIndex = 1;
};

/**
 * @brief Map is reponsible for
 *  . displaying the background which can be a GIS (googlemap), or a basic picture, or a geolocalized picture, with geonodes.
 *  . handling the display of the grid, the grames and the geonodes.
 *  It subscribes to Objects (layer.media) in order to be called back in case of creation or
 *  modification of the object, and it redraws what has changed.
 * @param {*} view
 * @param {*} svg
 * @param {Objects} objects instance of scene/Objects.js
 */
const Map = function (view, svg, objects) {
    let context = null;
    let grid = new Grid(view, svg, objects);
    let voronoi = new Voronoi(view, svg, objects, this);
    let image_node = new MapImageNode(view);
    let geonodes = new Geonodes(svg, objects);
    let map_type = "media"; // "media" or "gis"
    let map_gis = null; // MapGis instance if authorised

    log.debug("Map.ctor", {view, svg, objects});

    Object.defineProperties(this, {
        grid: {
            set: () => {},
            get: () => grid
        },
        voronoi: {
            set: () => {},
            get: () => voronoi
        },
        media_id: {
            set: () => {},
            get: () => image_node.media_id
        },
        rotation: {
            set: (value) => {
                image_node.rotation = value;
            },
            get: () => image_node.rotation
        },
        visibility: {
            set: (value) => {
                image_node.visibility = value;
            }
        },
        loaded: {
            set: () => {},
            get: () => image_node.media_id !== undefined || map_type == "gis"
        },
        width: {
            set: () => {},
            get: () => (map_type == "gis" && this.is_gis_authorized ? global.view.ol_driver.width : image_node.width)
        },
        height: {
            set: () => {},
            get: () => (map_type == "gis" && this.is_gis_authorized ? global.view.ol_driver.height : image_node.height)
        },
        brightness: {
            set: () => {},
            get: () => image_node.brightness
        },
        name: {
            set: (name) => {
                let object = this.get_map_object();
                if (object === null) {
                    return;
                }
                object.attributes.dataName = name;
                objects.tx_update("media", object);
            },
            get: () => BottomArea.tactical_situation_name
        },
        projection: {
            set: () => {},
            get: () => (context !== null ? context.projection : null)
        },
        geonodes: {
            set: () => {},
            get: () => geonodes
        },
        image_node: {
            set: () => {},
            get: () => image_node
        },
        is_gis: {
            // used only to know which menus are available. Use view.is_gis to know if we are in gis view and not gis static image
            set: () => {},
            get: () => (map_type == "gis" ? true : false)
        },
        is_gis_authorized: {
            // used to know if an org is authorized to use GIS or only fallback map
            set: () => {},
            get: () => {
                return global.view.is_gis_authorized;
            }
        },
        is_georeferenced: {
            set: () => {},
            get: () => {
                if (this.is_gis) {
                    return true;
                }
                if (context === null) {
                    return false;
                }
                if (context.projection === null) {
                    return false;
                }
                if (context.projection.projector === null) {
                    return false;
                }
                return true;
            }
        },
        map_type: {
            set: () => {},
            get: () => map_type
        },
        context: {
            set: () => {},
            get: () => context
        },
        map_gis: {
            set: () => {},
            get: () => map_gis
        }
    });

    /**
     * Set the name of the SITAC
     * @param {string} name
     */
    const set_map_object_name = (name) => {
        BottomArea.tactical_situation_name = name;
    };

    /**
     *
     * @param {string} type = "gis" or "media"
     */
    this.set_map_type = (type) => {
        log.debug("set_map_type", {type});
        map_type = type;
        // inform view
        global.view.set_view_type(type);
    };

    /**
     * Set the brightness of the background
     * @param {number} brightness, float value
     */
    this.set_map_object_brightness = (brightness) => {
        if (map_type == "gis" && map_gis) {
            map_gis.set_brightness(brightness);
        } else {
            image_node.brightness = brightness;
        }
    };

    /**
     * Reset the brightness of the background
     */
    this.unset_map_object_brightness = () => {
        if (map_type == "gis" && map_gis) {
            map_gis.unset_brightness();
        } else {
            image_node.brightness = 1;
        }
    };

    /**
     * Take into account grid_data modifications (in Objects) and redrax the grid if necessary
     * @param {*} grid_data in, one Object attribute
     */
    const update_common_grid_callback = async () => {
        log.debug("Map.update_common_grid_callback ");

        if (global.view.is_gis) {
            await grid.init_gis_from_common_grid();
        } else {
            await grid.update_image_from_common_grid();
        }
    };

    const unset_grid = () => {
        grid.configuration.unload();
        grid.undraw();
    };

    const load_projection = () => {
        log.debug("Map.load_projection");
        geonodes.load();
    };

    const unset_projection = () => {
        log.debug("Map.unset_projection");
        geonodes.load(); //TODO bug ?? probably not "load()", maybe "remove()" ???
    };

    /**
     * @brief Handle the "creating" event received after storage creation
     * @param local_id, the local_id of the object created (see indexedDB)
     * @param new_media, the object_data attributes that can be found in indexedDB storage
     */
    objects.layers.media.on("creating", async (local_id, new_map) => {
        log.debug("Map on creating new_map.dataType ", new_map.dataType);
        set_map_object_name(new_map.dataName);
        this.set_map_type(new_map.dataType); // needed for sharing gis static map to other organisations
        //if (new_media.dataMedia == "media-000102030405060708090a0b0c0d0e0f") { // this is for GIS

        if (new_map.dataType == "gis" && map_gis !== null) {
            log.debug("context.data.type ", context.data.type);
            await map_gis.on_creating_gis(local_id, new_map);
            if (global.server.network.state === "offline" || this.is_gis_authorized === false) {
                await this.on_creating_image(local_id, new_map);
            }
        } else {
            log.debug("context.data.type ", context.data.type);
            if (new_map.dataType === "gis") {
                context.data.type = "gis";
            }
            log.debug("context.data.type ", context.data.type);
            await this.on_creating_image(local_id, new_map);
        }

        this.set_map_object_brightness(new_map.dataBrightness); // dependant of map_type set in update_gis

        global.event_bus.publish("scene.map.state", {loaded: true});
    });

    /**
     * @brief Handle the "creating" event received after storage creation
     * @param local_id, the local_id of the object created (see indexedDB)
     * @param new_media, the object_data attributes that can be found in indexedDB storage
     */
    this.on_creating_image = async (local_id, new_map) => {
        log.debug("Map.on_creating_image in", JSON.stringify(new_map));
        //grid = new Grid(view, svg, this.width, this.height);
        //this.transform.append_child(grid.transform);

        // set image size to handle geonode rotation arround the center
        geonodes.image_width = new_map.dataGrid.grid.size.width;
        geonodes.image_height = new_map.dataGrid.grid.size.height;
        grid.is_extended = false; // reset is_extended for images

        // media is an image here
        await set_media_id(new_map.dataMedia);

        await load_projection();

        // init configuration stuff with provided grid
        grid.configuration.load(new_map.dataGrid);

        // check if there is a projectiona and a common grid and init all stuff
        await grid.init_image_from_common_grid(new_map.dataGrid);

        log.debug("Map.on_creating_image dataGrid ", new_map.dataGrid);
        grid.init_axis();
        grid.draw();

        global.tools.tools["grid_settings"].$button.css("display", null);
    };

    /**
     * Save in indexedDB and send to server
     */
    this.grid_save = () => {
        log.debug("Map.grid_save");
        this.set_map_object({});
    };

    /**
     *
     */
    this.common_grid_save_after_move = () => {
        // save "not common" attributes
        log.debug("Map.common_grid_save_after_move");

        if (global.view.is_gis) {
            grid.update_common_grid_from_gis_after_move();
        } else {
            if (grid.is_extended === true) {
                grid.init_common_grid_from_image();
            }
        }

        this.set_map_object({});
    };

    /**
     *
     */
    this.common_grid_save_after_resize = () => {
        // save "not common" attributes
        log.debug("Map.common_grid_save_after_resize");

        if (global.view.is_gis) {
            grid.update_common_grid_from_gis_after_resize();
        } else {
            if (grid.is_extended === true) {
                grid.update_common_grid_from_image_after_resize();
            }
        }

        this.set_map_object({});
    };

    /**
     * Callback when map object data is modified
     */
    objects.layers.media.on("updating", async (_local_id, _old_map, new_map) => {
        log.debug(`Map -> updating" new_map = ${JSON.stringify(new_map)}`);

        if ("dataName" in new_map) {
            set_map_object_name(new_map.dataName);
        }
        if ("dataBrightness" in new_map) {
            this.set_map_object_brightness(new_map.dataBrightness);
        }
        if ("dataMedia" in new_map) {
            await set_media_id(new_map.dataMedia);
            if (map_gis) {
                map_gis.set_fallback_media(new_map.dataMedia);
                global.view.save_map_gis_context(new_map.dataMedia);
            }
        }

        if ("dataGrid" in new_map) {
            log.debug("map updating grid draw");
            // redraw
            grid.draw();
        }

        if ("dataCommonGrid" in new_map) {
            // common grid has changed, recompute and draw the grid
            await update_common_grid_callback();
        }

        if ("dataProjection" in new_map) {
            await load_projection();
        }

        if ("dataGis" in new_map && map_gis) {
            await map_gis.on_updating_gis(new_map);
        }
        global.event_bus.publish("scene.map.state", {loaded: true});
    });

    /**
     * Callback when map object is deleted
     */
    objects.layers.media.on("deleting", async (local_id, old_map) => {
        log.debug("media deleting", {local_id, old_map});
        // map is a media
        if (!old_map.dataType == "gis" && !is_media_id(old_map.dataMedia)) {
            log.error(`mismatch of media id on deleting ${local_id} != ${old_map.dataMedia}`);
            return;
        }
        this.unset_map_object_brightness();
        unset_grid();
        unset_media_id();
        unset_projection();
        if (old_map.dataType == "gis" && this.is_gis_authorized) {
            await map_gis.on_deleting_gis(local_id, old_map);
        }
    });

    /**
     * center: set the map at the center of the window
     * @returns
     */
    this.center = () => {
        if (map_type == "gis" && global.server.network.state === "online" && this.is_gis_authorized) {
            let object = this.get_map_object();
            if (object === null) {
                return;
            }
            map_gis.center(object);
        } else {
            // center the image loaded
            if (this.rotation == 0) {
                let {width, height} = image_node;
                log.debug("node ", {width, height});
                if (width === undefined || height === undefined) {
                    log.error("can't center view no information about image width / height");
                    return;
                }
                global.view.d3_center_to_box({width, height});
            } else {
                // get the bounds of the rotated image
                let {x: ox, y: oy, width, height} = this.get_image_bounds();
                global.view.d3_center_to_box({width, height, ox, oy});
            }
        }
    };

    this.get_map_object = () => {
        //log.debug("get_background_image_object");
        if (objects.layers.media.ids.length > 1) {
            log.error("media layer has more than one images, this is probably a bug, please report it");
        }
        // the background image is the first object of layer "media"
        let local_id = objects.layers.media.ids[0];
        if (local_id === undefined) {
            return null;
        }
        return objects.layers.media.objects[local_id];
    };

    /**
     * @brief
     * @param {*} object
     * @param {*} media_id
     * @param {*} media_name
     * @param {*} brightness
     * @param {*} media_layer1_id
     * @param {*} media_layer2_id
     * @param {*} media_layer3_id
     * @returns
     */
    const update_map_object = (object, media_id, name, brightness, media_layer1_id, media_layer2_id, media_layer3_id) => {
        log.debug("update_map_object", {media_id, name, media_layer1_id, media_layer2_id, media_layer3_id});
        let attributes = JSON.parse(JSON.stringify(object.attributes));
        log.debug("update_map_object attributes = ", attributes);
        if (media_id !== undefined) {
            object.attributes.dataMedia = media_id;
        }
        if (name !== undefined) {
            object.attributes.dataName = name;
        }
        if (brightness !== undefined) {
            object.attributes.dataBrightness = brightness;
        }
        if (media_layer1_id !== undefined) {
            object.attributes.dataMediaLayer1 = media_layer1_id;
        }
        if (media_layer2_id !== undefined) {
            object.attributes.dataMediaLayer2 = media_layer2_id;
        }
        if (media_layer3_id !== undefined) {
            object.attributes.dataMediaLayer3 = media_layer3_id;
        }

        return objects.tx_update("media", object);
    };

    /**
     * Create or update and save the Map in Objects
     */
    this.set_map_object = ({media_id, name, media_info, brightness, media_layer1_id, media_layer2_id, media_layer3_id}) => {
        log.debug("set_map_object", {media_id, name});
        let object = this.get_map_object();
        if (object === null) {
            return create_map_object(media_id, name, media_info, brightness);
        }
        log.debug("set_map_object", object);
        return update_map_object(object, media_id, name, brightness, media_layer1_id, media_layer2_id, media_layer3_id);
    };

    this.edit_map_object = async (editor) => {
        log.debug("edit_map_object");
        var object = this.get_map_object();
        if (object === null) {
            return;
        }
        editor(object);
        return objects.tx_update("media", object);
    };

    this.brightness_minus = () => {
        return this.edit_map_object((object) => {
            object.attributes.dataBrightness -= 0.2;
        });
    };

    this.brightness_plus = () => {
        return this.edit_map_object((object) => {
            object.attributes.dataBrightness += 0.2;
        });
    };

    this.brightness_reset = () => {
        return this.set_map_object({brightness: 1});
    };

    /**
     * @brief Re-init grid attributes with configuration values.
     * Take into account grid direction for origin and resized_grid
     *
     */
    this.grid_reset = () => {
        return this.edit_map_object((object) => {
            log.debug("grid_reset in");
            let data_grid = grid.get_default_configuration();
            if (object.attributes.dataType === "gis") {
                data_grid.grid.color = "#305dd0"; // blue  = default grid color for GIS
            }
            log.debug(`grid_reset frame.reference.position = ${grid.configuration.get("frame.reference.position")}`);
            data_grid.grid.size.width = this.width;
            data_grid.grid.size.height = this.height;
            data_grid.grid.common_size.width = this.width;
            data_grid.grid.common_size.height = this.height;
            // AGPOC-4607 : pb with GIGN reset grid => reset also grid origin
            // grid reset cannot work if user org = GIGN and shared background_image has been created by another org
            switch (grid.configuration.get("frame.reference.position")) {
                case DIRECTION_NORTH_WEST: // standard
                    break;
                case DIRECTION_SOUTH_WEST: // GIGN
                    data_grid.origin.y = this.height;
                    data_grid.resized_grid.height = this.height;
                    break;
                case DIRECTION_SOUTH_EAST:
                    data_grid.origin.x = this.width;
                    data_grid.origin.y = this.height;
                    data_grid.resized_grid.width = this.width;
                    data_grid.resized_grid.height = this.height;
                    break;
                case DIRECTION_NORTH_EAST:
                    data_grid.origin.x = this.width;
                    data_grid.resized_grid.width = this.width;
                    break;
            }
            log.debug("grid_reset out data_grid", data_grid);
            Object.assign(object.attributes.dataGrid, data_grid);
        });
    };

    /**
     * Change the background context to another SITAC / index
     * @param new_context The context of the new SITAC / index
     * @returns {Promise<void>}
     */
    this.change_context = async (new_context) => {
        log.debug("change_context", {new_context});
        var old_context = context;
        context = new_context;
        if (new_context === old_context) {
            return;
        }
        var old_context_is_empty = old_context === null || (await old_context.is_empty());
        var new_context_is_empty = new_context === null || (await new_context.is_empty());
        log.debug("change_context", {old_context_is_empty, new_context_is_empty});
        // AGPOC-3024 : doesn't seem to be useful. Probably not since adding of launch_transform(0,0,1) at several places
        /*if (old_context_is_empty && new_context_is_empty) {
            reset_view_transform();
        }*/
        if (new_context_is_empty) {
            $("#map_area button.add-media").css("display", null);
            $("#map_area button.add-gis").css("display", null);
            unset_grid();
            set_map_object_name(null);
        }
        await geonodes.change_context(new_context);
    };

    this.transform = new Transform({x: 0, y: 0, k: 1});
    this.transform.append_child(grid.transform);
    this.transform.append_child(voronoi.transform);
    this.transform.append_child(image_node.transform);

    // MapGis module created if needed
    if (this.is_gis_authorized) {
        map_gis = new MapGis(view, svg, objects, this);
    }

    /**
     * After click on the add-media button; select and load the image
     */
    $("body").on("click", "#map_area button.add-media", async () => this.initialize());

    this.initialize = async (file, name) => {
        log.debug("Map.initialize");

        var media = await this.select(file);
        if (LoadingMediaArea.interaction !== null) {
            LoadingMediaArea.interaction.close();
        }
        if (media === null) {
            return;
        }

        name ??= await InputTextArea.interact({
            tip: global.internationalization.text("sitac.Tactical-situation-name"),
            current_text: media.media_name
        }); // if nullish
        if (name === null) {
            log.debug("Cancelling map initialization...");
            set_map_object_name(null);
            global.medias.unload_media({local_id: media.local_id});
            objects.tx_delete("media", this.get_map_object()).then(() => {
                global.event_bus.publish("scene.map.initialized", {loaded: false});
            });
            $("#map_area button.add-media").show();
            $("#map_area button.add-gis").show();
            return;
        }
        if (media.media_name !== name) {
            await this.set_map_object({name});
        }
        // Note: the map type is done in set_media_id called by on_creating_image
    };

    /**
     * @brief display the file selection window and load the image of the selected file
     * @param {string} file
     * @returns {Promise<*|null>}
     */
    this.select = async (file) => {
        var media = null;
        log.debug("select", {file});
        if (file !== undefined) {
            media = await global.medias.handle_file(file);
        } else {
            media = await global.medias.interact({accept: "image/*"});
        }
        if (media === null) {
            return null;
        }
        log.debug("select: media selected = ", {media});
        if (this.get_map_object() !== null) {
            await AlertArea.interact({
                type: "danger",
                tip1: "Une image a déjà été selectionée pour cette situation tactique." //TODO internationalization MJO
            });
            return null;
        }
        global.view.d3_center_to_box(media.media_info);
        await this.set_map_object({
            media_id: media.local_id,
            name: media.media_name,
            media_info: media.media_info
        });
        log.debug("map media ID should be up to date", global.storage.selected_space.selected_context.map_media_id);
        // Fix AGPOC-7978: desktop selector status not updated after initialization with picture.
        global.event_bus.publish("scene.map.initialized", {loaded: true});
        // TODO: Simplify initialization and reduce the number of redondant published events
        return media;
    };

    /**
     * create_map_object: create a map (former background image) object
     * @param {string} media_id, kind of GUID
     * @param {string} name name provided by the form
     * @param {width,height} media_info size of the media
     * @param {number} brightness float value
     * @returns
     */
    const create_map_object = async (media_id, name, media_info, brightness) => {
        log.debug("Map.create_map_object", {media_id, name, media_info});
        if (media_id === undefined) {
            throw new Error.build.invalid({
                message: "background image media_id is required, can't continue sorry"
            });
        }
        if (name === undefined) {
            throw new Error.build.invalid({
                message: "background image name is required, can't continue sorry"
            });
        }
        if (brightness === undefined) {
            brightness = 1;
        }

        // get a clone of default configuration
        let data_grid = grid.get_default_configuration();
        data_grid.grid.size.width = media_info.width;
        data_grid.grid.size.height = media_info.height;

        // init for none extended grid
        data_grid.grid.common_size.width = media_info.width;
        data_grid.grid.common_size.height = media_info.height;
        log.debug("Map.create_map_object: grid_data = ", data_grid);

        // if a common grid exists in another SITAC, use its frame.reference.position
        // common grid frame.reference.psotion may come from an organization with a different value
        let common_grid_str = await global.storage.selected_space.get_grid_common_data();
        if (common_grid_str !== undefined) {
            let common_grid = JSON.parse(common_grid_str);
            if (common_grid.reference_position !== undefined) {
                data_grid.frame.reference.position = common_grid.reference_position;
                data_grid.frame.color = common_grid.reference_colors;
                log.debug(`Map.create_map_object: use common frame.reference.position = ${common_grid.reference_position}`);
            }
        }

        grid.is_extended = false;
        grid.configuration.load(data_grid);
        grid.set_picture_grid_origin();

        log.debug("object.tx_create: media");
        return objects.tx_create("media", {
            dataMedia: media_id,
            dataType: "media",
            dataSubtype: "background_image",
            dataName: name,
            dataBrightness: brightness,
            dataGrid: data_grid,
            dataProjection: null
        });
    };

    /**
     * Set the media id of the image
     * Notify that the image is loaded
     * @param media_id
     * @returns {Promise<void>}
     */
    const set_media_id = async (media_id) => {
        log.debug("set_media_id", {media_id});
        // if mode GIS map and network online
        //if (map_type === "gis" && global.server.network.state === "online" && this.is_gis_authorized) {
        if (map_type === "gis" && global.view.is_gis) {
            log.debug("set media id GIS => return");
            return;
        }
        var media = await global.medias.load_media({local_id: media_id});
        log.debug("set_media_id after load, media = ", {media});
        if (media === undefined) {
            return;
        }

        log.debug("context.data.type ", context.data.type);
        if (context.data.type === "gis") {
            // mode GIS cache
            this.set_map_type("gis");
        } else {
            // mode background image
            this.set_map_type("media");
        }

        // check context
        if (!global.view.apply_context()) {
            // no context, it is a shared background
            global.view.d3_center_to_box(media);
            global.view.transform_apply();
        }
        image_node.set_media(media);
        voronoi.init();
        $("#map_area button.add-media").hide();
        $("#map_area button.add-gis").hide();
    };

    /**
     * Unset the media id of the image
     * Notify that the image is no more loaded
     * @param media_id
     * @returns {Promise<void>}
     */
    const unset_media_id = () => {
        log.debug("unset_media_id");
        voronoi.deinit();
        image_node.unset_media();
        global.event_bus.publish("scene.map.state", {loaded: false});
    };

    const is_media_id = (media_id) => image_node.media_id === media_id;

    /**
     * @brief Get the bounding box of the background picture, taking into account the rotation. The result doesn't depends
     *        on the transformations (translate and scale)
     * @returns floats tuple with:
     *          x,y: top-left position of the box, in the D3 coordinate system
     *          width,height: size of the box
     */
    this.get_image_bounds = () => {
        let img = document.getElementById("background_image");

        // get bounding box with the transformation (x,y,k)
        let bb = img.getBoundingClientRect();
        // find the coordinates (x1,y1) of the topleft of the boundingbox without transformation
        let {x, y} = global.scene.transform_coordinates({x: bb.left, y: bb.top}, {ox: 0, oy: 0});

        // find the size (w,h) of the boundingbox without transformation
        let {x: x2, y: y2} = global.scene.transform_coordinates({x: bb.right, y: bb.bottom}, {ox: 0, oy: 0});
        let width = x2 - x;
        let height = y2 - y;
        log.debug("get_image_bounds returns ", {x, y, width, height});

        return {x, y, width, height};
    };
};

export default Map;
